import {
    Attribute,
    ChangeDetectionStrategy,
    Component,
    Directive,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewEncapsulation
} from '@angular/core';
import { AbstractControl, UntypedFormBuilder } from '@angular/forms';
import { buildProviders} from '@kalidea/kaligraphi';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, tap } from 'rxjs/operators';
import { CustomFormElement } from 'src/app/_core/tools/custom-form-element';
import {
    isOperandMandatory,
    isRangeValid,
    RangeComparatorData,
    rangeComparatorsList
} from 'src/app/utils/range.utils';
import isEqual from 'lodash-es/isEqual';

export type RangeType = 'number' | 'date';

// noinspection AngularMissingOrInvalidDeclarationInModule
@Directive()
export class SharedComparedRangeComponent<T extends RangeType>
    extends CustomFormElement<RangeComparatorData<T>>
    implements OnInit {

    // liste des opérateurs pour le template
    comparatorsList = rangeComparatorsList;

    // émission de la valeur du comparateur
    @Output() readonly valueChanges = new EventEmitter<RangeComparatorData<T>>();
    isOperandMandatory = isOperandMandatory;

    readonly formGroup = this.fb.group({
        comparator: [null],
        min: [null],
        max: [null],
    }, {updateOn: 'blur'});

    constructor(
        // on utilise attribute car le type n'est pas modifiable "by design"
        @Attribute('type') public readonly type: string,
        private readonly fb: UntypedFormBuilder
    ) {
        super();
    }

    @Input()
    get value(): RangeComparatorData<T> {
        return this.formGroup.value;
    }

    /**
     * pour éviter de faire un set "null" ou d'avoir une donnée qui manque,
     * on destructure la valeur actuelle et on lui ajoute les nouvelles données
     */
    set value(value: RangeComparatorData<T>) {
        this.setValue(value);
    }

    writeValue(value: RangeComparatorData<T>): void {
        if(!isEqual(value, this.value)) {
            this.setValue(value);
        }
    }

    validate(c: AbstractControl): Observable<Record<string, any>> {
        return of(isRangeValid(this.value) ? null : {minormax: {value: this.value}});
    }

    protected setValue(value: RangeComparatorData<T>, options?: {
        onlySelf?: boolean,
        emitEvent?: boolean,
    }): void {
        if( !isEqual(this.value, value)) {
            this.formGroup.patchValue(
                {...this.value, ...value || {comparator: null, min: null, max: null}},
                options
            );
            this.valueChanges.emit(value);
        }
    }

    /**
     * corrige la valeur stockée pour respecter le format attendu
     */
    private fixValue(): void {
        const {comparator} = this.value;
        // si on a pas encore de comparateur, c'est pas la peine de continuer
        if (!comparator) {
            return;
        }
        const fixedValue: RangeComparatorData<T> = {comparator, min: null, max: null};
        (['min', 'max'] as Exclude<(keyof RangeComparatorData<T>), 'comparator'>[]).forEach(bound => {
            if (isOperandMandatory(this.value, bound)) {
                fixedValue[bound] = this.value[bound];
            }
        });
        this.setValue(fixedValue, {emitEvent: false});
    }

    ngOnInit(): void {
        this.watch$ = this.formGroup.valueChanges.pipe(
            distinctUntilChanged(isEqual),
            tap(() => {
                if (isRangeValid(this.value)) {
                    this.fixValue();
                    this.valueChanges.emit(this.value);
                    this.notifyUpdate(this.value);
                }
            })
        );
    }
}

@Component({
    selector: 'app-shared-compared-range[type=number]',
    templateUrl: './shared-compared-range.component.html',
    styleUrls: ['./shared-compared-range.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(SharedComparedRangeNumberComponent),
    standalone: false
})
export class SharedComparedRangeNumberComponent extends SharedComparedRangeComponent<'number'> {

    constructor(
        // on utilise attribute car le type n'est pas modifiable "by design"
        @Attribute('type') readonly type: string,
        fb: UntypedFormBuilder
    ) {
        super(type, fb);
    }
}

@Component({
    selector: 'app-shared-compared-range[type=date]',
    templateUrl: './shared-compared-range.component.html',
    styleUrls: ['./shared-compared-range.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(SharedComparedRangeDateComponent),
    standalone: false
})
export class SharedComparedRangeDateComponent extends SharedComparedRangeComponent<'date'> {
    constructor(
        // on utilise attribute car le type n'est pas modifiable "by design"
        @Attribute('type') readonly type: string,
        fb: UntypedFormBuilder
    ) {
        super(type, fb);
    }
}
